import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AionaLogo from '../assets/aiona/AIONA_白.png';
import { useAppDispatch, useAppSelector } from '@store/index';
import { login, clearError, selectAuthLoading, selectAuthError, selectIsAuthenticated } from '@features/auth/authSlice';
import { clearSessionNotifications } from '@features/ui/uiSlice';
import { LoginRequest } from '../types';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
    tenant_code: '',
    remember_me: false,
  });
  const [showPassword, setShowPassword] = useState(false);

  // Get redirect location from state
  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    // Clear any previous errors
    dispatch(clearError());
    dispatch(clearSessionNotifications());
  }, [dispatch]);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData((prev: LoginRequest) => ({
      ...prev,
      [name]: name === 'remember_me' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearSessionNotifications());
    dispatch(login(formData));
  };

  const isFormValid = formData.email && formData.password && formData.tenant_code;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', overflow: 'hidden' }}>
        <Box sx={{
          bgcolor: 'primary.main',
          p: 3,
          display: 'flex',
          justifyContent: 'center',
        }}>
          <img src={AionaLogo} alt="AIONA" style={{ height: 50 }} />
        </Box>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {t('auth.signInPrompt')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('auth.tenantCode')}
              name="tenant_code"
              value={formData.tenant_code}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="organization"
              placeholder={t('auth.tenantCodePlaceholder')}
            />

            <TextField
              fullWidth
              label={t('auth.emailAddress')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
            />

            <TextField
              fullWidth
              label={t('auth.password')}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('auth.togglePasswordVisibility')}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="remember_me"
                  checked={formData.remember_me}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label={t('auth.rememberMe')}
              sx={{ mt: 1, mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!isFormValid || isLoading}
              sx={{ mt: 2 }}
            >
              {isLoading ? <CircularProgress size={24} /> : t('auth.signIn')}
            </Button>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                color="primary"
                onClick={() => navigate('/forgot-password')}
                sx={{ textTransform: 'none' }}
              >
                {t('auth.forgotPassword')}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
